import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { UserSettings } from 'app/global/user-settings.dto';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, EMPTY, firstValueFrom, from, merge, Observable, of, switchMap, tap } from 'rxjs';
import { catchError, map, mergeMap, take, takeUntil } from 'rxjs/operators';
import { CoreSidebarService } from '../../../../../@core/components/core-sidebar/core-sidebar.service';
import { UserService } from '../../../../auth/service';
import { GlobalService } from '../../../../global/global.service';
import { PusherService } from 'app/global/pusher.service';
import { environment } from 'environments/environment';
import { AdvancedFilterHelper, ContainsCategoryHelper, DocumentTypeHelper } from '../../../shared/advanced-filters/advanced-filter.helper';
import {
    AdvancedFilterFieldTypesEnum,
    AdvanceFilterParamType,
    IAdvanceFilter,
    IConditionAdvancedFilter,
    IGeneralDataAdvancedFilter,
    IMetadataAdvancedFilters,
    IOrganizationDataAdvancedFilter,
    IPersonDataAdvancedFilter
} from '../../../shared/advanced-filters/components/advance-filter';
import { IGetSavedListingFiltersResponseDTO } from '../../../shared/save-listing-filters/save-filters.dto';
import { ICustomColumn } from '../../../shared/shared/table/components/custom-columns-settings/column-settings';
import { ITableColumn } from '../../../shared/shared/table/table-column';
import { IGetCategoriesItemDto } from '../../categories.dto';
import { CategoryService } from '../../category.service';
import { ContractControlService } from '../../contract-control/contract-control.service';
import { ExportCsvModalComponent } from '../../contract-control/modals/export-csv-modal/export-csv-modal.component';
import { IExportCSVPreview } from '../../contract-control/modals/export-csv-modal/IExport-csv-preview';
import { ICCExportPreview } from '../../contract-control/store/dto/export-preview';
import { ManagerDTO } from '../../contract-control/store/dto/manager-list.dto';
import { DocumentFieldService } from '../../settings/documentfields/documentfield.service';
import { IMetadataItemDto, IMetadataListDTO, MetaDataTypeEnum } from '../../settings/documentfields/metadata-item.dto';
import { DocumentTypeService } from '../../settings/documenttype/document-type.service';
import { ISearchableRequest } from '../../users/userslisting/dto/get-user-list.dto';
import { DocumentListingApiService } from '../document-listing.api.service';
import { DocumentsService } from '../documents.service';
import { IDocumentDetailRootDTO } from '../dto/document-details.dto';
import { IDocumentDto, IDocumentTypesDto, IUserGroupFilter, OwnerFilterType } from '../dto/document-listing.dto';
import { FilterDocumentStatusesEnum } from '../dto/filter-document-statuses.enum';
import * as AuthSelector from './../../../../store/selectors/auth.selectors';
import * as AuthUserSelectors from './../../../../store/selectors/auth.selectors';
import * as SavedFiltersActions from './../../../shared/save-listing-filters/store/saved-filters.actions';
import * as SavedFiltersSelector from './../../../shared/save-listing-filters/store/saved-filters.selectors';
import * as DocumentsListingActions from './document-listing.actions';
import { DocumentListingFacade, IUpdateFieldsData } from './document-listing.facade';
import {
    DocumentViewVariantEnum,
    IAdvancedSearchRequest,
    IDocumentExportPreview,
    IDocumentFilterState,
    IDocumentPreviewRequest,
    IExportDocumentsRequest
} from './document-listing.reducers';
import * as DocumentsListingSelectors from './document-listing.selectors';
import { constAdvancedFilters } from './document-listing.selectors';
import { GetDocumentsRequestBuilder } from './get-documents-request-builder';
import { DocumentHelperFacade } from '../document-right-sidebar/document-helper.facade';
import { StatusCodes } from 'app/auth/interceptors/status-codes';
import { ValidationHandlerService } from 'app/global/validation-handler/validation-handler.service';
import { DocumentStatusesEnum } from '../dto/document-statuses.enum';
import { ValidationErrorPrefix } from 'app/global/validation-handler/validation-error.dto';
import { DocumentDetailActions } from './document-listing.actions';
import { PlaybookListeningApiService } from '../../legal-hub/playbook-listening.api.service';
import { DocumentTypeSectionEnum } from '../../settings/documenttype/document-type-settings-modal/default-constants/document-type-settings.constants';


type ExportDocumentModalType = ExportCsvModalComponent<IDocumentExportPreview, ManagerDTO>;

@Injectable({
    providedIn: 'root'
})
export class DocumentListingEffects {
    private containsCategoryHelper: ContainsCategoryHelper = new ContainsCategoryHelper();
    private documentTypeHelper: DocumentTypeHelper = new DocumentTypeHelper();

    constructor(private _actions$: Actions,
        private _store: Store,
        private _globalService: GlobalService,
        private _documentListingApiService: DocumentListingApiService,
        private _coreSidebarService: CoreSidebarService,
        private _documentService: DocumentsService,
        private _modalService: NgbModal,
        private _documentListingFacade: DocumentListingFacade,
        private _translocoService: TranslocoService,
        private _documentFieldService: DocumentFieldService,
        private _contractControlService: ContractControlService,
        private _categoryService: CategoryService,
        private _documentTypeService: DocumentTypeService,
        private _userService: UserService,
        private _advancedFilterHelper: AdvancedFilterHelper,
        private _toastr: ToastrService,
        private _documentHelperFacade: DocumentHelperFacade,
        private _validationHandler: ValidationHandlerService,
        private _pusherService: PusherService
    ) {
    }

    fetchDocuments$ = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(DocumentsListingActions.fetchDocuments),
                switchMap(() => {
                    return combineLatest({
                        search: this._store.select(DocumentsListingSelectors.search),
                        paging: this._store.select(DocumentsListingSelectors.paging),
                        filters: this._store.select(DocumentsListingSelectors.filterBy),
                        viewVariant: this._store.select(DocumentsListingSelectors.activeView),
                        advancedFilters: this._store.select(DocumentsListingSelectors.appliedAdvancedFilters),
                    }).pipe(take(1))
                }),
                switchMap(({ search, paging, filters, viewVariant, advancedFilters }) => {
                    const params = new GetDocumentsRequestBuilder()
                        .setSearchValue(search)
                        .setBasicFilter(filters)
                        .setAdvancedFilter(advancedFilters)
                        .setPaging(paging?.currentPage || null)
                        .build();


                    return this._documentListingApiService.getDocuments(viewVariant, params)
                        .pipe(
                            map(result => DocumentsListingActions.fetchDocumentsSucceeded(result)),
                            catchError((error) => of(DocumentsListingActions.fetchDocumentsFailed(error)))
                        )
                }
                )
            )
    });


    initPusher$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.fetchDocumentsSucceeded),
            take(1),
            switchMap(() => {
                return this._pusherService.listen<{ id: number, status: DocumentStatusesEnum, updated_at: string }>(environment.pusherChannel, 'document-status-updated')
                    .pipe(
                        map(data => {
                            if (data?.id && data.status) {
                                return DocumentsListingActions.updateDocumentStatusFromPusher(data.id, data.status, data.updated_at);
                            }
                            return { type: 'NO_ACTION' };
                        })
                    );
            })
        );
    });

    refreshDocuments$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.deleteSelectedDocumentsSucceeded, DocumentsListingActions.restoreSelectedDocumentsSucceeded),
            map(() => DocumentsListingActions.fetchDocuments())
        );
    });


    fetchDocumentsFailed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.fetchDocumentsFailed),
            tap(({ error }) => {
                if (error?.status === StatusCodes.UNPROCESSABLE_ENTITY) {
                    this._globalService.toastrError(this._translocoService.translate('Toaster message for advance filter validation (Advanced filters) - The filter values you entered are not valid'));
                } else {
                    this._globalService.toastrError(this.getErrorMessages(error?.message));
                }
            })
        );
    }, {
        dispatch: false
    });


    pageChange$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.changePage),
            map(() => DocumentsListingActions.fetchDocuments()
            )
        );
    });



    /**
     * Handles document selection UI logic (sidebar toggling)
     */
    onDocumentSelected$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.documentClicked),
            tap(({ rightBarName }) => {
                const instance = this._coreSidebarService.getSidebarRegistry(rightBarName);
                if (instance?.isOpened) {
                    this._store.dispatch(DocumentsListingActions.documentClicked(null, ''))
                }
                instance.toggleOpen();
            })
        );
    }, { dispatch: false });

    /**
     * Fetches document data from the API and transforms it.
     * This effect can be reused by dispatching DocumentsListingActions.fetchDocumentData() or documentClicked()
     */
    fetchDocumentData$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.documentClicked, DocumentsListingActions.fetchDocumentData),
            switchMap((args) => {
                return this._documentService.getDocument(args?.documentId).pipe(
                    map(x => {
                        return {
                            ...x,
                            detailFieldValues: x.detailFieldValues.map((item) => {
                                if (item.metadata.meta_data_type === MetaDataTypeEnum.checkbox) {
                                    item.value = item.value === '1' ? 1 : 0;
                                    item.old_value = item.old_value === '1' ? 1 : 0;
                                }
                                return item;
                            })
                        }
                    }),
                    map(document => DocumentsListingActions.fetchDocumentDataSucceeded(<IDocumentDetailRootDTO>document)),
                    catchError(error => of(DocumentsListingActions.fetchDocumentDataFailed(error)))
                )
            })
        );
    });


    documentIndexData$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.documentIndexDataCall),
            switchMap((params) => {
                return this._documentService.getDocumentIndexData(params.documentId)
                    .pipe(
                        map((indexData) => {
                            const result = { ...indexData };
                            if ((result).template_fields_datas) {
                                (result).template_fields_datas.data = (typeof (result).template_fields_datas.data === 'string') ? JSON.parse((result).template_fields_datas.data) : result.template_fields_datas.data;
                            }
                            return result; //TODO: verify mapping
                        }),
                        map((indexData) => DocumentsListingActions.documentIndexSucceeded(indexData)),
                        catchError(error => of(DocumentsListingActions.documentIndexFailed(error)))
                    )
            })
        )
    })


    makeDocumentRevoked$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.makeDocumentRevoked),
            switchMap((params) => {
                return this._documentService.documentRevoke(params?.documentId)
                    .pipe(
                        map(() => DocumentsListingActions.makeDocumentRevokedSucceeeded(params?.documentId, params.status)),
                        catchError(error => of(DocumentsListingActions.makeDocumentRevokedFailed(error)))
                    )
            })
        )
    });


    updateDocumentStatus$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateDocumentStatus),
            switchMap(params => {
                return this._documentService.updateDocumentStatus(params.documentId, params.status).pipe(
                    map(() => DocumentsListingActions.updateDocumentStatusSucceeeded(params.documentId, params.status)),
                    catchError(error => of(DocumentsListingActions.updateDocumentStatusFailed(error)))
                )
            })
        )
    })


    updateDocumentStatusFailed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateDocumentStatusFailed),
            tap(args => {
                this._globalService.toastrError(args.error)
            })
        )
    }, { dispatch: false });


    updateDocumentType$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateDocumentType),
            switchMap(params => {
                return this._documentService.updateDocumentType(params.documentId, params.documentType.id).pipe(
                    map(() => DocumentsListingActions.updateDocumentTypeSucceeeded(params.documentId, params.documentType)),
                    catchError(error => of(DocumentsListingActions.updateDocumentTypeFailed(error)))
                )
            })
        )
    });

    updateDocumentTypeSuccess$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateDocumentTypeSucceeeded),
            tap(() => {
                this._globalService.toastrSuccess(this._translocoService.translate('Updating document type (Document right sidebar) Document type updated successfully'));
            })
        )
    }, { dispatch: false });


    updateDocumentTypeFailed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateDocumentTypeFailed, DocumentsListingActions.updateCategoryFailed),
            tap(args => {
                this._globalService.toastrError(args.error)
            })
        )
    }, { dispatch: false })


    updateDocumentDueDate$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateDocumentDueDate),
            switchMap(params => {
                return this._documentService.updateDueDate({ documentId: params.documentId, due_date: params.due_date })
                    .pipe(
                        map((response) => DocumentsListingActions.updateDocumentDueDateSucceeeded(params.due_date, response.document_status)),
                        catchError(error => of(DocumentsListingActions.updateDocumentDueDateFailed(error)))
                    )
            })
        )
    });


    updateDueDateRequestError$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateDocumentDueDateFailed),
            tap(({error}) => {
                const errorMessage = typeof error === 'string'
                    ? error
                    : error?.error?.message || this._translocoService.translate('Something went wrong (general toast error) - Something went wrong');
                this._globalService.toastrError(errorMessage)
            })
        )
    }, { dispatch: false });


    updateDocumentVisibility$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateDocumentVisibility),
            switchMap((params) => {
                return this._documentService.updateDocumentVisibility({
                    documentId: params.documentId,
                    visibility: params.visibility
                }).pipe(
                    map(() => DocumentsListingActions.updateDocumentVisibilitySucceeeded(params.documentId, params.visibility)),
                    catchError(error => of(DocumentsListingActions.updateDocumentVisibilityError(error)))
                )
            })
        )
    });


    updateDocumentVisibilityError$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateDocumentVisibilityError),
            tap(args => {
                this._globalService.toastrError(args.error)
            })
        )
    }
        , { dispatch: false });


    updateTagsInDocument$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateTagsInDocument),
            switchMap((params) => {
                return this._documentService.addTags(params.documentId, params.tag).pipe(
                    map(() => DocumentsListingActions.updateTagsInDocumentSucceeeded(params.documentId, params.tag)),
                    catchError(error => of(DocumentsListingActions.updateTagsInDocumentFailed(error)))
                )
            })
        )
    });


    updateTagsInDocumentFailed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateTagsInDocumentFailed),
            tap(args => {
                this._globalService.toastrError(args.error)
            })
        )
    }, { dispatch: false });


    removeTagFromDocument$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.removeTagFromDocument),
            mergeMap(params => {
                return this._documentService.removeTag(params.documentId, params.tag).pipe(
                    map(() => DocumentsListingActions.removeTagFromDocumentSucceeeded(params.tag)),
                    catchError(error => of(DocumentsListingActions.removeTagFromDocumentFailed(error)))
                )
            })
        )
    });


    removeTagFromDocumentFailed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.removeTagFromDocumentFailed),
            tap(args => this._globalService.toastrError(args.error))
        )
    }, { dispatch: false });


    connectContract$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.connectContract),
            switchMap(params => {
                return this._contractControlService.multisectionadd(params.formData).pipe(
                    map(() => DocumentsListingActions.connectContractSuccess([params.formData])),
                    catchError(error => of(DocumentsListingActions.connectContractFailure(error)))
                )
            })
        )
    });


    connectContractFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.connectContractFailure),
            tap(args => {
                this._globalService.toastrError(args.error);
            })
        )
    }, { dispatch: false });


    updateDocumentName$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateDocumentName),
            switchMap(params => {
                return this._documentService.updateDocumentName(params.documentId, params.name).pipe(
                    map(() => DocumentsListingActions.updateDocumentNameSuccess(params.documentId, params.name)),
                    catchError(error => of(DocumentsListingActions.updateDocumentNameFailure(error)))
                )
            })
        )
    });


    updateDocumentNameFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateDocumentNameFailure),
            tap(args => {
                this._globalService.toastrError(args.error);
            })
        )
    }, { dispatch: false });


    documentTypesFetch$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.fetchDocumentTypes),
            switchMap(() =>
                this._documentListingApiService.getDocumentsTypes()
                    .pipe(
                        map(types => DocumentsListingActions.fetchDocumentTypesSucceeded(types)),
                        catchError(error => of(DocumentsListingActions.fetchDocumentTypesFailed(error)))
                    )
            )
        )
    });

    ownerFilterSearch$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.ownerSearchChanged),
            switchMap(() => of(DocumentsListingActions.fetchUserGroups())))
    });

    usersFetch$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.fetchUserGroups),
            switchMap(() => this._store.select(DocumentsListingSelectors.ownerFilterSearch)),
            switchMap((search) => combineLatest({
                groups: this._store.select(AuthUserSelectors.userGroups),
                users: this._userService.usersAutocomplete(true, search)
                    .pipe(
                        map((x) => x.data.map(y => ({ id: y.id, name: y.name }))
                        ))
            }).pipe(
                map(result => DocumentsListingActions.fetchUsersSucceeded(({
                    users: result.users,
                    groups: result.groups
                }))),
                catchError(error => of(DocumentsListingActions.fetchUsersFailed(error)))
            )
            )
        )
    });


    clearSelectedFilter$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                DocumentsListingActions.searchChange,
                DocumentsListingActions.filterByStatus,
                DocumentsListingActions.filterByFromDate,
                DocumentsListingActions.filterByToDate,
                DocumentsListingActions.filterByDocumentType,
                DocumentsListingActions.filterByUser,
                DocumentsListingActions.filterByCategory,
                DocumentsListingActions.updateAdvancedFilter
            ),
            tap(() => this._store.dispatch(SavedFiltersActions.clearSelectedFilter()))
        )
    }, {
        dispatch: false
    });

    filterByChanged$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                DocumentsListingActions.searchChange,
                DocumentsListingActions.filterByStatus,
                DocumentsListingActions.filterByFromDate,
                DocumentsListingActions.filterByToDate,
                DocumentsListingActions.filterByDocumentType,
                DocumentsListingActions.filterByUser,
                DocumentsListingActions.filterByCategory,
                DocumentsListingActions.setPerPageRecords
            ),
            map(() => DocumentsListingActions.fetchDocuments())
        )
    });


    changeActiveView$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.changeActiveView),
            tap(({ view }) => {
                if (view === DocumentViewVariantEnum.table_view) {
                }
            })
        )
    }, {
        dispatch: false
    });


    deleteSelectedDocuments$ = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(DocumentsListingActions.deleteSelectedDocuments),
                switchMap(() => {
                    return this._store.select(DocumentsListingSelectors.selection)
                        .pipe(take(1))
                }),
                switchMap((documents: IDocumentDto[]) => {
                    return this._documentListingApiService.deleteDocuments(documents.map(x => x.id))
                        .pipe(
                            map(() => DocumentsListingActions.deleteSelectedDocumentsSucceeded()),
                            catchError((error) => of(DocumentsListingActions.deleteSelectedDocumentsFailed(error)))
                        )
                }
                )
            )
    });

    deleteSelectedDocumentsSucceeded$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.deleteSelectedDocumentsSucceeded, DocumentsListingActions.deleteDocumentSuccess),
            tap(() => {
                this._globalService.toastrSuccess(this._translocoService.translate('Document deleted toast (document listing) - Documents deleted successfully'));
            })
        );
    }, {
        dispatch: false
    });


    deleteSelectedDocumentsFailed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.deleteSelectedDocumentsFailed, DocumentsListingActions.deleteDocumentFailure),
            tap(({ error }) => {
                this._globalService.toastrError(this.getErrorMessages(error?.message || this._translocoService.translate('Document delete fail toast (document listing) - Could not delete documents')));
            })
        );
    }, {
        dispatch: false
    });


    restoreSelectedDocuments$ = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(DocumentsListingActions.restoreSelectedDocuments),
                switchMap(() => {
                    return this._store.select(DocumentsListingSelectors.selection)
                        .pipe(take(1))
                }),
                switchMap((documents: IDocumentDto[]) => {
                    return this._documentListingApiService.restoreDocuments(documents.map(x => x.id))
                        .pipe(
                            map(() => DocumentsListingActions.restoreSelectedDocumentsSucceeded()),
                            catchError((error) => of(DocumentsListingActions.restoreSelectedDocumentsFailed(error)))
                        )
                }
                )
            )
    });


    restoreSelectedDocumentsSucceeded$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.restoreSelectedDocumentsSucceeded),
            tap(() => {
                this._globalService.toastrSuccess(this._translocoService.translate("Document restore toast (document listing) - Documents restored successfully"));
            })
        );
    }, {
        dispatch: false
    });


    restoreSelectedDocumentsFailed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.restoreSelectedDocumentsFailed),
            tap(({ error }) => {
                this._globalService.toastrError(this.getErrorMessages(error?.message || this._translocoService.translate('Document restore fail toast (document listing) - Could not restore documents')));
            })
        );
    }, {
        dispatch: false
    });


    fetchCustomColumns$ = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(DocumentsListingActions.fetchCustomColumns),
                switchMap(() => {
                    return this._documentListingApiService.getCustomColumns()
                        .pipe(
                            map((columns) => DocumentsListingActions.fetchCustomColumnsSucceeded({ columns })),
                            catchError((error) => of(DocumentsListingActions.fetchCustomColumnsFailed(error)))
                        )
                }
                )
            )
    });

    fetchCustomColumnsFailed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.fetchCustomColumnsFailed),
            tap(({ error }) => {
                this._globalService.toastrError(this.getErrorMessages(error?.message || this._translocoService.translate('Document table column fetch fail (document listing) - Could not fetch column options')));
            })
        );
    }, {
        dispatch: false
    });


    updateCustomColumns$ = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(DocumentsListingActions.updateCustomColumns),
                switchMap(({ column_settings }) => {
                    //@TODO: Will remove this code after updated data will saved to DB correctly
                    const LEGACY_VALUES = [0, '0', '1'];
                    const updateColumnSetting = column_settings.map(item => {
                        // 1. Determine the new 'type' first.
                        // We check the original 'item.type' to see if it's a legacy value.
                        const newType = LEGACY_VALUES.includes(item?.type) ? 'default' : item?.type;

                        // 2. Determine the new 'doc_id'.
                        // The 'doc_id' should be null if it's a legacy value OR if the *final* type is 'default'.
                        const isLegacyDocId = LEGACY_VALUES.includes(item?.doc_id);
                        const newDocId = (isLegacyDocId || newType === 'default') ? null : item?.doc_id;

                        // 3. Return a new object instead of mutating the (cloned) item.
                        // This is a cleaner, more functional approach.
                        return {
                            ...item,
                            type: newType,
                            doc_id: newDocId,
                        };
                    });
                    return this._documentListingApiService.updateCustomColumns(updateColumnSetting)
                        .pipe(
                            map(() => DocumentsListingActions.updateCustomColumnsSucceeded({
                                column_settings: updateColumnSetting
                            })),
                            catchError((error) => of(DocumentsListingActions.updateCustomColumnsFailed(error)))
                        )
                }
                )
            )
    });

    updateColumnsFailed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateCustomColumnsFailed),
            tap(({ error }) => {
                this._globalService.toastrError(this.getErrorMessages(error?.message || this._translocoService.translate('Document table column update fail (document listing) - Could not fetch column options')));
            })
        );
    }, {
        dispatch: false
    });


    exportCSV$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.exportCSV),
            tap(({ format }) => {
                const instance = this._modalService.open(ExportCsvModalComponent, {
                    size: 'xl',
                    windowClass: 'export-csv-modal',
                });

                (instance.componentInstance as ExportDocumentModalType).exportFormat = format;

                const modalClosed$ = from(instance.result).pipe(
                    catchError(() => of(null)),
                    take(1),
                );

                (instance.componentInstance as ExportDocumentModalType).export
                    .pipe(
                        switchMap(exportOptions => this.onDataExport(instance, exportOptions as IExportDocumentsRequest, format, modalClosed$)),
                        takeUntil(modalClosed$)
                    )
                    .subscribe();
                (instance.componentInstance as ExportDocumentModalType).pageChange
                    .pipe(
                        tap(() => (instance.componentInstance as ExportDocumentModalType).loading = true),
                        switchMap(page =>
                            this.onExportPreviewPageChange(page)
                                .pipe(
                                    take(1),
                                    catchError(() => {
                                        this._globalService.toastrError(this._translocoService.translate('Document fetch export preview fails (document export modal) - Could not fetch export preview'));
                                        return of(null);
                                    })
                                )
                        ),
                        takeUntil(modalClosed$)
                    )
                    .subscribe(response => {
                        (instance.componentInstance as ExportDocumentModalType).loading = false;
                        (instance.componentInstance as ExportDocumentModalType).data = response;
                        if (response?.data?.length > 0) {
                            (instance.componentInstance as ExportDocumentModalType).columns = this.computePreviewCSVColumns(response.data[0]);
                        }
                    });
            })
        )
    },
        {
            dispatch: false
        });

    exportDocumentsData$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.exportDocumentsData),
            switchMap(({ exportOptions }) => this._documentListingApiService.exportDocuments(exportOptions)
                .pipe(
                    map(() => DocumentsListingActions.exportDocumentsDataSuccess()),
                    catchError(error => of(DocumentsListingActions.exportDocumentsDataFailure({ error: error })))
                )
            )
        )
    });

    exportDocumentsDataFailed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.exportDocumentsDataFailure),
            tap(({ error }) => {
                this._globalService.toastrError(error || this._translocoService.translate('Something went wrong message (general toast) - Something went wrong'));
            })
        )
    }, {
        dispatch: false
    });

    exportDocumentsDataSuccess$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.exportDocumentsDataSuccess),
            tap(() => {
                this._globalService.toastrSuccess(this._translocoService.translate('Document export scheduled (document export modal) - Export has been scheduled successfully. An email will be send when data will be ready'));
            })
        )
    }, {
        dispatch: false
    });

    deleteDocument = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.deleteDocument),
            switchMap((action) => {
                return this._documentListingApiService.deleteDocument(action.documentId)
                    .pipe(
                        map(() => DocumentsListingActions.deleteDocumentSuccess()),
                        catchError((error) => of(DocumentsListingActions.deleteDocumentFailure(error)))
                    )
            })
        );
    });

    deleteDocumentSuccess = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(DocumentsListingActions.deleteDocumentSuccess),
                tap(() => {
                    const modal = this._coreSidebarService.getSidebarRegistry('document-right-sidebar');
                    if (modal) {
                        modal.close();
                        this._store.dispatch(DocumentsListingActions.fetchDocuments());
                    }
                }))
    }, {
        dispatch: false
    });

    fetchAdvancedFiltersColumns$ = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(DocumentsListingActions.fetchAdvancedFilters),
                switchMap(() => combineLatest({
                    search: this._store.select(DocumentsListingSelectors.advancedFilterSearch),
                    page: this._store.select(DocumentsListingSelectors.advancedFilterPage)
                })),
                switchMap(({ page, search }) => {
                    return this._documentFieldService.documentfields(page, search)
                        .pipe(
                            map((metadata: IMetadataListDTO) => DocumentsListingActions.fetchAdvancedFiltersSuccess({ metadata })),
                            catchError((error) => of(DocumentsListingActions.fetchAdvancedFiltersFailure(error)))
                        )
                }
                )
            )
    });

    advancedFilterSearch$ = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(DocumentsListingActions.advancedFilterSearch),
                map(() => DocumentsListingActions.fetchAdvancedFilters())
            )
    });

    advancedFiltersChange = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(
                    DocumentsListingActions.removeAdvancedFilter,
                ),
                switchMap(() => this._documentListingFacade.appliedAdvancedFilters$
                    .pipe(take(1),
                        map(x => x.every(y => y.isApplied === true))
                    )
                ),
                tap(allCorrect => {
                    if (allCorrect === true) {
                        this._store.dispatch(DocumentsListingActions.fetchDocuments());
                    }
                }),
            )
    }, {
        dispatch: false
    });

    restoreFilter = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.restoreFilter),
            switchMap(({ filterId }) => filterId
                ? combineLatest({
                    filter: this._store.select(SavedFiltersSelector.selectFilter(filterId)).pipe(take(1)),
                    currentUser: this._store.select(AuthSelector.userDataSelector),
                    documentTypes: this._documentListingFacade.documentTypes$,
                    categories: this._documentListingFacade.categories$
                }).pipe(take(1))
                : of(null)
            ),
            tap(async (params: { filter: IGetSavedListingFiltersResponseDTO, currentUser: UserSettings, documentTypes: IDocumentTypesDto[], categories: IGetCategoriesItemDto[] }) => {
                this._documentListingFacade.setBasicDocumentFilters(this.retrieveBasicFilters(params?.filter?.value, params?.currentUser, params?.documentTypes, params?.categories));
                if (params?.filter?.value?.advanced) {
                    this._documentListingFacade.setAdvancedFilters(await this.retrieveAdvancedFilters(params?.filter.value.advanced));
                } else {
                    this._documentListingFacade.setAdvancedFilters([]);
                }
                this._store.dispatch(SavedFiltersActions.filterSelected({ filter: params?.filter || null }));
                this._documentListingFacade.fetchDocuments();
            })
        )
    }, {
        dispatch: false
    });

    deleteSavedFilter = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(DocumentsListingActions.deleteFilter),
                map(({ filterId }) => SavedFiltersActions.deleteSavedFilter({ filterId }))
            )
    });

    editSavedFilter = createEffect(() => {
        return this._actions$
            .pipe(
                ofType(DocumentsListingActions.editFilter),
                map(({ filter }) => SavedFiltersActions.editSavedFilter({ filter }))
            )
    });

    updateDocumentColumnValue$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateDocumentColumnValue),
            switchMap(({ formData }) => {
                return this._documentService.updatePreviewColumnValue(formData)
                    .pipe(
                        map(() => DocumentsListingActions.updateDocumentColumnValueSuccess({ formData })),
                        catchError((error) => of(DocumentsListingActions.updateDocumentColumnValueFailure(error)))
                    )
            })
        )
    });




    updateDocumentColumnValueFailed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateDocumentColumnValueFailure),
            tap(({ error }) => {
                this._globalService.toastrError(this.getErrorMessages(error?.message || this._translocoService.translate('Document table column update fail (document listing) - Could not fetch column options')));
            }
            )
        );
    }, { dispatch: false });



    updateFieldsData$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.updateFieldsData),
            switchMap(({ payload }) => this._documentService.updateFieldsData({
                dataable_type: 'document',
                dataable_id: payload.dataable_id,
                field_type: 'document_field',
                data: payload?.filteredFields,
                deleted_field_uuid: payload?.deleted_field_uuid,
            }).pipe(
                map(() => DocumentsListingActions.updateFieldsDataSuccess({ payload })),
                catchError((error) => {
                    if (error?.status === StatusCodes.UNPROCESSABLE_ENTITY) {
                        this._validationHandler.handleValidationErrors(error?.error, true, ValidationErrorPrefix.Data);
                    } else {
                        this._globalService.toastrError(error);
                    }
                    const payload = { data: this._globalService.fields_data } as IUpdateFieldsData;
                    return of(DocumentsListingActions.updateFieldsDataFailure({ error, ...{ payload } }))
                })
            )
            )
        )
    });

    checkSpecialActions$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.checkSpecialActions),
            switchMap((action) =>
                this._documentService.checkSpecialActions(action.documentId).pipe(
                    map((response) => DocumentsListingActions.updateSpecialActions({ action: response?.additional_action })),
                    catchError(() => of(DocumentsListingActions.updateSpecialActions({ action: null })))
                )
            )
        );
    });

    resendToMetodika$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.resendToMetodika),
            switchMap((action) =>
                this._documentService.resendToMetodika(action.documentId).pipe(
                    map(() => DocumentsListingActions.resendToMetodikaSuccess()),
                    catchError((error) => of(DocumentsListingActions.resendToMetodikaFailure({ error })))
                )
            )
        );
    });

    resendToMetodikaSuccess$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.resendToMetodikaSuccess),
            tap(() => {
                this._globalService.toastrSuccess(this._translocoService.translate('Successfully resent to Metodika toastr (Documents Section) - Successfully sent to Metodika'));
            })
        );
    }, { dispatch: false });

    resendToMetodikaFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentsListingActions.resendToMetodikaFailure),
            tap(({ error }) => {
                // The interceptor may return either:
                // 1. The full error object (for certain status codes)
                // 2. Just the message string (extracted by the interceptor)
                let errorMessage: string;

                if (typeof error === 'string') {
                    // Interceptor already extracted the message
                    errorMessage = error;
                } else if (error?.error?.message) {
                    // Full error object with nested message
                    errorMessage = error.error.message;
                } else if (error?.message) {
                    // Error object with message property
                    errorMessage = error.message;
                } else {
                    // Fallback
                    errorMessage = this._translocoService.translate('Failed to resend to Metodika toastr (Documents Section) - Failed to resend to Metodika');
                }

                this._globalService.toastrError(errorMessage);
            })
        );
    }, { dispatch: false });








    updateDocumentEnitityData$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentDetailActions.updateDocumentEntityData),
            switchMap(({ documentId, settings }) => {
                return this._documentService.updateDocumentEntityData(documentId, settings).pipe(
                    map((response) => DocumentDetailActions.updateDocumentEntityDataSuccess({ settings: response?.settings ?? {}, categories: response?.updated_categories ?? [] })),
                    catchError((error) => of(DocumentDetailActions.updateDocumentEntityDataFailure({ error })))
                );
            })
        )
    });



    updateDocumentEntityDataFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentDetailActions.updateDocumentEntityDataFailure),
            tap(({ error }) => {
                this._globalService.toastrError(error?.error?.message || 'Failed to update document entity data');
            })
        )
    }, { dispatch: false });



    changeDocumentType$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DocumentDetailActions.changeDocumentType),
            switchMap(({id, documentId}) => {
                return combineLatest({
                    id: of(id),
                    documentId: of(documentId)
                }).pipe(take(1))
            }),
            switchMap(({ documentId, id }) => {
                return this._documentTypeService.getDocumentTypeById(id, `?section=document&id=${documentId}`).pipe(
                    map((documentType) => DocumentDetailActions.changeDocumentTypeSuccess({ groups: documentType.sections.find(section => section.name === DocumentTypeSectionEnum.DocumentLayout)?.groups ?? [] })),
                    catchError((error) => of(DocumentDetailActions.changeDocumentTypeFailure({ error })))
                );
            })
        )
    });


    private retrieveBasicFilters(value: IAdvancedSearchRequest, currentUser: UserSettings, documentTypes: IDocumentTypesDto[], categories: IGetCategoriesItemDto[]): Partial<IDocumentFilterState & ISearchableRequest> {
        const filters = {} as Partial<IDocumentFilterState & ISearchableRequest>;

        if (value && Object.prototype.hasOwnProperty.call(value, 'search') && (<string>value.search)?.length > 0) {
            filters.search = value.search as string;
        } else {
            delete filters.search;
        }

        if (value && Object.prototype.hasOwnProperty.call(value, 'per_page_records') && value.per_page_records) {
            filters.perPageRecords = value.per_page_records as number
        } else {
            delete filters.perPageRecords;
        }

        if (value && Object.prototype.hasOwnProperty.call(value, 'status') && value.status) {
            const isIncluded = Object.values(FilterDocumentStatusesEnum).map(x => x.toLocaleLowerCase().trim()).includes(value.status.toLocaleLowerCase().trim());
            if (!isIncluded) {
                this._toastr.warning(this._translocoService.translate('Category not restored notification by saved filters (CC and Documents default filters) - Saved category filter was not be restored'));
                delete filters.status;
            } else {
                filters.status = value.status as FilterDocumentStatusesEnum;
            }
        } else {
            delete filters.status;
        }

        if (value && Object.prototype.hasOwnProperty.call(value, 'date_from') && value.date_from) {
            filters.dateFrom = value.date_from as string;
        } else {
            delete filters.dateFrom;
        }

        if (value && Object.prototype.hasOwnProperty.call(value, 'date_to') && value.date_to) {
            filters.dateTo = value.date_to as string;
        } else {
            delete filters.dateTo;
        }

        if (value && Object.prototype.hasOwnProperty.call(value, 'document_type_id') && value.document_type_id) {
            const isIncluded = this.documentTypeHelper.includeDocumentType(value.document_type_id, documentTypes);
            if (!isIncluded) {
                this._toastr.warning(this._translocoService.translate('Document type not restored notification by saved filters (CC and Documents default filters) - Saved document type filter was not be restored'));
                delete filters.documentTypeId;
            } else {
                filters.documentTypeId = value.document_type_id as number;
            }
        } else {
            delete filters.documentTypeId;
        }

        if (value && Object.prototype.hasOwnProperty.call(value, 'owners_from_group_id') && value.owners_from_group_id) {
            filters.userFilter = {
                id: value.owners_from_group_id,
                name: ' ',
                group: OwnerFilterType.group
            } as IUserGroupFilter
        } else if (value && Object.prototype.hasOwnProperty.call(value, 'owner_id') && value.owner_id) {
            filters.userFilter = {
                id: value.owner_id,
                name: '',
                group: value.owner_id === currentUser.id ? OwnerFilterType.me : OwnerFilterType.user
            } as IUserGroupFilter
        } else {
            delete filters.userFilter;
        }

        if (value && Object.prototype.hasOwnProperty.call(value, 'category_id') && value.category_id) {
            if (value.category_id === 'no_category') {
                filters.categoryId = -1;
            } else {
                const isIncluded = this.containsCategoryHelper.includeCategory(value.category_id, categories);
                if (!isIncluded) {
                    this._toastr.warning(this._translocoService.translate('Category not restored notification by saved filters (CC and Documents default filters) - Saved category filter was not be restored'));
                    delete filters.categoryId;
                } else {
                    filters.categoryId = value.category_id as number;
                }
            }
        } else {
            delete filters.categoryId;
        }

        return filters;
    }




    private retrieveAdvancedFiltersDocumentField(advanced: AdvanceFilterParamType, index: number, metadataList: IMetadataItemDto[]) {

        const documentFieldItem = metadataList.find(x => x?.section === AdvancedFilterFieldTypesEnum.document_field && x?.id === (<IMetadataAdvancedFilters>advanced)?.meta_data_id);

        if (documentFieldItem) {
            return {
                uuid: index.toString(),
                field_type: AdvancedFilterFieldTypesEnum.document_field,
                isApplied: true,
                isOpened: false,
                item: documentFieldItem as unknown as IPersonDataAdvancedFilter,
                text: (documentFieldItem)?.meta_data_name,
                value: (advanced as IMetadataAdvancedFilters)?.value,
                operator: (advanced as IMetadataAdvancedFilters)?.operator
            } as IAdvanceFilter<IPersonDataAdvancedFilter>;
        } else {
            return of(null);
        }

    }

    private async retrieveAdvancedFilters(advanced: AdvanceFilterParamType[]): Promise<Partial<IAdvanceFilter<AdvanceFilterParamType>[]>> {
        const all_advanced_filters_list = await this.mapSavedAdvancedFilters(advanced);
        return this._advancedFilterHelper.validateRestoredAdvancedFilters(all_advanced_filters_list ?? []);
    }

    private async mapSavedAdvancedFilters(advanced) {
        const metaDataList = await firstValueFrom(this._documentFieldService.getAllDocumentfields(1, '', `all=true`))

        return Promise.all(advanced.map(async (x, index) => {
            switch ((x as IAdvanceFilter<AdvanceFilterParamType>).field_type) {
                case AdvancedFilterFieldTypesEnum.document_field: {
                    return this.retrieveAdvancedFiltersDocumentField(x, index, metaDataList ?? []);
                }
                case AdvancedFilterFieldTypesEnum.organization_field: {
                    const item3 = constAdvancedFilters.find(z => {
                        return z.field_type === AdvancedFilterFieldTypesEnum.organization_field && (z.source as IOrganizationDataAdvancedFilter).organization_data_type === (x as IOrganizationDataAdvancedFilter).organization_data_type
                    });

                    if (item3) {
                        return {
                            uuid: index.toString(),
                            field_type: AdvancedFilterFieldTypesEnum.organization_field,
                            isApplied: true,
                            isOpened: false,
                            icon: item3.icon,
                            item: item3.source,
                            text: item3.text,
                            value: (x as IPersonDataAdvancedFilter).value,
                            operator: (x as IPersonDataAdvancedFilter).operator,
                        } as IAdvanceFilter<IPersonDataAdvancedFilter>;
                    } else {
                        return null
                    }


                }
                case AdvancedFilterFieldTypesEnum.person_field: {
                    const item2 = constAdvancedFilters.find(z => {
                        return z.field_type === AdvancedFilterFieldTypesEnum.person_field && (z.source as IPersonDataAdvancedFilter).person_data_type === (x as IPersonDataAdvancedFilter).person_data_type
                    });

                    if (item2) {
                        return {
                            uuid: index.toString(),
                            field_type: AdvancedFilterFieldTypesEnum.person_field,
                            isApplied: true,
                            isOpened: false,
                            icon: item2.icon,
                            item: item2.source,
                            text: item2.text,
                            value: (x as IPersonDataAdvancedFilter).value,
                            operator: (x as IPersonDataAdvancedFilter).operator
                        } as IAdvanceFilter<IPersonDataAdvancedFilter>;
                    } else {
                        return null;
                    }

                }
                case AdvancedFilterFieldTypesEnum.general_field: {
                    const item = constAdvancedFilters.find(z => {
                        return z.field_type === AdvancedFilterFieldTypesEnum.general_field && (z.source as IGeneralDataAdvancedFilter).general_data_type === (x as IGeneralDataAdvancedFilter).general_data_type
                    });

                    if (item) {
                        return {
                            uuid: index.toString(),
                            field_type: AdvancedFilterFieldTypesEnum.general_field,
                            isApplied: true,
                            isOpened: false,
                            icon: item.icon,
                            item: item.source,
                            text: item.text,
                            value: (x as IGeneralDataAdvancedFilter).value,
                            operator: (x as IGeneralDataAdvancedFilter).operator
                        } as IAdvanceFilter<IGeneralDataAdvancedFilter>;
                    } else {
                        return null
                    }

                }
                case AdvancedFilterFieldTypesEnum.condition:
                    return {
                        uuid: index.toString(),
                        condition_type: (x as IConditionAdvancedFilter).condition_type,
                        field_type: AdvancedFilterFieldTypesEnum.condition,
                        isApplied: true,
                        isOpened: false,
                        item: {},
                        text: ''
                    } as IAdvanceFilter<IConditionAdvancedFilter>
            }
        }));
    }

    private getErrorMessages(errorMessage: string): string {
        return errorMessage || this._translocoService.translate('Something went wrong message (general toast) - Something went wrong');
    }

    private onExportPreviewPageChange(page: number): Observable<IExportCSVPreview<IDocumentPreviewRequest>> {
        return this.fetchExportPreview(page);
    }

    private fetchExportPreview(page: number = 1) {
        return combineLatest({
            search: this._store.select(DocumentsListingSelectors.search),
            filters: this._store.select(DocumentsListingSelectors.filterBy),
            advancedFilters: this._store.select(DocumentsListingSelectors.appliedAdvancedFilters),
        }).pipe(

            switchMap(({ search, filters, advancedFilters }) => {
                const params = new GetDocumentsRequestBuilder()
                    .setSearchValue(search)
                    .setBasicFilter(filters)
                    .setAdvancedFilter(advancedFilters)
                    .build();

                return this._documentListingApiService.exportPreview({
                    page: page,
                    ignored_contracts: [],
                    ...params
                });
            }),
        )
    }

    private computePreviewCSVColumns(row: ICCExportPreview): ITableColumn[] {
        const dataObjectKeys = Object.keys(row);
        return dataObjectKeys.map(x => ({
            field: x,
            title: x,
            cellCssClass: 'column-min-width',
            headerCssClass: 'column-min-width'
        }));
    }

    private onDataExport(instance: NgbModalRef, exportOptions: IExportDocumentsRequest, format: 'csv' | 'xlsx', modalClosed$: Observable<unknown>) {
        return combineLatest({
            search: this._store.select(DocumentsListingSelectors.search),
            paging: this._store.select(DocumentsListingSelectors.paging),
            filters: this._store.select(DocumentsListingSelectors.filterBy),
            viewVariant: this._store.select(DocumentsListingSelectors.activeView)
        })
            .pipe(
                take(1),
                switchMap(({ search, paging, filters }) => {
                    const params = new GetDocumentsRequestBuilder()
                        .setSearchValue(search)
                        .setBasicFilter(filters)
                        .setPaging(paging?.currentPage || null)
                        .build();


                    this._documentListingFacade.exportData({
                        ...params,
                        ...exportOptions,
                        format,
                        ignored_contracts: []
                    });

                    return merge(
                        this._actions$.pipe(ofType(DocumentsListingActions.exportDocumentsDataSuccess), map(() => true)),
                        this._actions$.pipe(ofType(DocumentsListingActions.exportDocumentsDataFailure), map(() => false)),
                    ).pipe(
                        take(1),
                        takeUntil(modalClosed$),
                        tap((isSuccess) => {
                            if (isSuccess) {
                                instance.close();
                            }
                        }),
                    );
                })
            )
    }


}
